import { supabase } from "@/integrations/supabase/client";

export interface SessionReview {
  session_id: string;
  player_id: string;
  mentor_id: string;
  rating: number;
  comment: string;
  created_at?: string;
}

/**
 * Marca uma sessão como concluída pelo jogador
 */
export async function markSessionAsCompleted(sessionId: string): Promise<void> {
  try {
    console.log(`🔄 [markSessionAsCompleted] Marcando sessão ${sessionId} como concluída`);
    
    // Primeiro, verificar se a sessão pertence ao usuário logado
    const { data: currentUser } = await supabase.auth.getUser();
    if (!currentUser?.user) {
      throw new Error('Usuário não autenticado');
    }
    
    console.log(`👤 [markSessionAsCompleted] Usuário logado: ${currentUser.user.id}`);
    
    // Tentar usar uma função RPC se disponível
    try {
      const { data: rpcResult, error: rpcError } = await supabase
        .rpc('mark_session_completed' as any, { 
          session_id: sessionId,
          player_id: currentUser.user.id 
        });
        
      if (!rpcError) {
        console.log(`✅ [markSessionAsCompleted] Sessão marcada como concluída via RPC`);
        return;
      }
      
      console.log(`⚠️ [markSessionAsCompleted] RPC não disponível, tentando UPDATE direto:`, rpcError);
    } catch (rpcError) {
      console.log(`⚠️ [markSessionAsCompleted] RPC falhou, tentando UPDATE direto:`, rpcError);
    }
    
    // Verificar se a sessão existe e pertence ao usuário
    const { data: sessionCheck, error: checkError } = await supabase
      .from("sessions")
      .select("id, player_profile_id, status")
      .eq("id", sessionId)
      .eq("player_profile_id", currentUser.user.id)
      .single();
      
    if (checkError) {
      console.error(`❌ [markSessionAsCompleted] Erro ao verificar sessão:`, checkError);
      throw new Error('Sessão não encontrada ou não pertence ao usuário');
    }
    
    if (!sessionCheck) {
      throw new Error('Sessão não encontrada');
    }
    
    console.log(`✅ [markSessionAsCompleted] Sessão encontrada:`, sessionCheck);
    
    // Tentar diferentes abordagens para atualizar
    const updateApproaches = [
      // Abordagem 1: Update simples apenas com status
      async () => {
        const { error } = await supabase
          .from("sessions")
          .update({ status: "concluido" })
          .eq("id", sessionId)
          .eq("player_profile_id", currentUser.user.id);
        return error;
      },
      
      // Abordagem 2: Update usando eq duplo
      async () => {
        const { error } = await supabase
          .from("sessions")
          .update({ status: "concluido" })
          .match({ 
            id: sessionId, 
            player_profile_id: currentUser.user.id 
          });
        return error;
      },
      
      // Abordagem 3: Update com upsert
      async () => {
        const { error } = await supabase
          .from("sessions")
          .upsert({ 
            id: sessionId,
            status: "concluido" 
          } as any, {
            onConflict: 'id'
          });
        return error;
      }
    ];
    
    let lastError = null;
    for (let i = 0; i < updateApproaches.length; i++) {
      try {
        console.log(`🔄 [markSessionAsCompleted] Tentativa ${i + 1} de atualização`);
        const error = await updateApproaches[i]();
        
        if (!error) {
          console.log(`✅ [markSessionAsCompleted] Sessão ${sessionId} marcada como concluída com sucesso na tentativa ${i + 1}`);
          return;
        }
        
        lastError = error;
        console.error(`❌ [markSessionAsCompleted] Tentativa ${i + 1} falhou:`, error);
      } catch (attemptError) {
        lastError = attemptError;
        console.error(`❌ [markSessionAsCompleted] Tentativa ${i + 1} erro:`, attemptError);
      }
    }
    
    // Se chegou aqui, todas as tentativas falharam
    console.error(`❌ [markSessionAsCompleted] Todas as tentativas falharam. Último erro:`, lastError);
    throw lastError || new Error('Falha ao atualizar sessão');
    
  } catch (error) {
    console.error(`❌ [markSessionAsCompleted] Erro geral:`, error);
    throw error;
  }
}

/**
 * Cria uma avaliação para a sessão
 */
export async function createSessionReview(review: SessionReview): Promise<void> {
  try {
    console.log(`📝 [createSessionReview] Criando avaliação para sessão ${review.session_id}`);
    console.log(`📝 [createSessionReview] Dados da avaliação:`, review);
    
    // Verificar se usuário está autenticado
    const { data: currentUser } = await supabase.auth.getUser();
    if (!currentUser?.user) {
      throw new Error('Usuário não autenticado');
    }
    
    console.log(`🔍 [createSessionReview] Usuário autenticado: ${currentUser.user.id}`);
    
    // Primeiro, verificar se a tabela session_reviews existe
    const { data: tableCheck, error: tableError } = await supabase
      .from("session_reviews")
      .select("id")
      .limit(1);
      
    if (tableError && tableError.code === '42P01') {
      console.error(`❌ [createSessionReview] Tabela session_reviews não existe:`, tableError);
      throw new Error('Sistema de avaliações não configurado. Entre em contato com o suporte.');
    }
    
    console.log(`✅ [createSessionReview] Tabela session_reviews existe`);
    
    // Verificar se a sessão existe e pertence ao usuário
    console.log(`🔍 [createSessionReview] Verificando sessão ${review.session_id} para usuário ${currentUser.user.id}`);
    
    const { data: sessionData, error: sessionError } = await supabase
      .from("sessions")
      .select("id, player_profile_id, mentor_id, status")
      .eq("id", review.session_id)
      .eq("player_profile_id", currentUser.user.id)
      .single();
    
    if (sessionError) {
      console.error(`❌ [createSessionReview] Erro ao verificar sessão:`, sessionError);
      console.error(`❌ [createSessionReview] Código do erro:`, sessionError.code);
      console.error(`❌ [createSessionReview] Mensagem do erro:`, sessionError.message);
      console.error(`❌ [createSessionReview] Detalhes completos:`, sessionError);
      
      // Tentar buscar a sessão sem o filtro de player_profile_id para ver se ela existe
      const { data: sessionCheck, error: checkError } = await supabase
        .from("sessions")
        .select("id, player_profile_id, mentor_id, status")
        .eq("id", review.session_id)
        .single();
        
      if (checkError) {
        console.error(`❌ [createSessionReview] Sessão não existe:`, checkError);
        throw new Error('Sessão não encontrada no sistema');
      } else {
        console.log(`🔍 [createSessionReview] Sessão existe mas não pertence ao usuário:`, sessionCheck);
        console.log(`🔍 [createSessionReview] Player ID da sessão: ${sessionCheck.player_profile_id}`);
        console.log(`🔍 [createSessionReview] Player ID atual: ${currentUser.user.id}`);
        throw new Error('Sessão não pertence ao usuário logado');
      }
    }
    
    if (!sessionData) {
      throw new Error('Sessão não encontrada');
    }
    
    if (sessionData.status !== 'concluido') {
      throw new Error('Sessão ainda não foi concluída');
    }
    
    console.log(`✅ [createSessionReview] Sessão encontrada:`, sessionData);
    
    // Determinar mentor_id - usar o da sessão ou do review
    const mentorInfoId = sessionData.mentor_id || review.mentor_id;
    
    console.log(`🔍 [createSessionReview] Mentor Info ID (mentor_info.id): ${mentorInfoId}`);
    
    // Buscar o profile_id do mentor a partir do mentor_info.id
    // session_reviews.mentor_id deve referenciar profiles.id, não mentor_info.id
    let mentorProfileId: string | null = null;
    
    if (mentorInfoId) {
      console.log(`🔍 [createSessionReview] Buscando profile_id do mentor...`);
      const { data: mentorInfo, error: mentorInfoError } = await supabase
        .from("mentor_info")
        .select("profile_id")
        .eq("id", mentorInfoId)
        .single();
      
      if (mentorInfoError) {
        console.error(`❌ [createSessionReview] Erro ao buscar mentor_info:`, mentorInfoError);
        console.warn(`⚠️ [createSessionReview] Continuando sem mentor_id...`);
      } else if (mentorInfo?.profile_id) {
        mentorProfileId = mentorInfo.profile_id;
        console.log(`✅ [createSessionReview] Profile ID do mentor encontrado: ${mentorProfileId}`);
      } else {
        console.warn(`⚠️ [createSessionReview] Profile ID do mentor não encontrado`);
      }
    }
    
    // Verificar se já existe avaliação para esta sessão
    const { data: existingReview, error: checkError } = await supabase
      .from("session_reviews")
      .select("id")
      .eq("session_id", review.session_id)
      .eq("player_id", currentUser.user.id)
      .single();
      
    if (checkError && checkError.code !== 'PGRST116') {
      console.error(`❌ [createSessionReview] Erro ao verificar avaliação existente:`, checkError);
      // Continue mesmo com erro na verificação
    }
    
    if (existingReview) {
      console.warn(`⚠️ [createSessionReview] Avaliação já existe para sessão ${review.session_id}`);
      throw new Error('Você já avaliou esta sessão');
    }
    
    // Função para tentar inserir com mentor_id específico
    const tryInsertReview = async (mentorId: string | null) => {
      const reviewData = {
        session_id: review.session_id,
        player_id: currentUser.user.id,
        mentor_id: mentorId,
        rating: review.rating,
        comment: review.comment,
        created_at: new Date().toISOString()
      };
      
      console.log(`📝 [createSessionReview] Tentando inserir com mentor_id: ${mentorId || 'NULL'}`);
      console.log(`📝 [createSessionReview] Dados para inserção:`, reviewData);
      
      return await supabase
        .from("session_reviews")
        .insert([reviewData])
        .select();
    };
    
    // Validar dados obrigatórios
    if (review.rating < 1 || review.rating > 5) {
      throw new Error('Avaliação deve ser entre 1 e 5 estrelas');
    }
    
    if (!review.comment.trim()) {
      throw new Error('Comentário é obrigatório');
    }
    
    // Inserir com o profile_id do mentor (não o mentor_info.id)
    console.log(`💾 [createSessionReview] Inserindo avaliação com mentor_profile_id: ${mentorProfileId || 'NULL'}...`);
    let { data: insertData, error: insertError } = await tryInsertReview(mentorProfileId);
    
    // Se deu erro de chave estrangeira, tentar com mentor_id NULL
    if (insertError && insertError.code === '23503' && insertError.message.includes('mentor_id')) {
      console.warn(`⚠️ [createSessionReview] Mentor ${mentorProfileId} não existe, tentando com NULL...`);
      
      const result = await tryInsertReview(null);
      insertData = result.data;
      insertError = result.error;
    }
      
    if (insertError) {
      console.error(`❌ [createSessionReview] Erro ao criar avaliação:`, insertError);
      console.error(`❌ [createSessionReview] Código do erro de inserção:`, insertError.code);
      console.error(`❌ [createSessionReview] Mensagem do erro de inserção:`, insertError.message);
      console.error(`❌ [createSessionReview] Detalhes completos do erro:`, insertError);
      
      // Tratar erros específicos
      if (insertError.code === '23505') {
        throw new Error('Você já avaliou esta sessão');
      }
      
      if (insertError.code === '23503') {
        if (insertError.message.includes('session_id')) {
          throw new Error('Sessão não encontrada no sistema');
        } else if (insertError.message.includes('player_id')) {
          throw new Error('Usuário não encontrado no sistema');
        } else {
          throw new Error('Dados de referência inválidos');
        }
      }
      
      if (insertError.code === '23514') {
        throw new Error('Avaliação deve ser entre 1 e 5 estrelas');
      }
      
      throw new Error(`Erro ao salvar avaliação: ${insertError.message}`);
    }
    
    console.log(`✅ [createSessionReview] Avaliação criada com sucesso:`, insertData);
  } catch (error) {
    console.error(`❌ [createSessionReview] Erro geral:`, error);
    throw error;
  }
}

/**
 * Libera o pagamento de uma sessão
 */
export async function releaseSessionPayment(sessionId: string): Promise<void> {
  try {
    console.log(`💰 [releaseSessionPayment] Liberando pagamento para sessão ${sessionId}`);
    
    // Verificar se a sessão pertence ao usuário logado
    const { data: currentUser } = await supabase.auth.getUser();
    if (!currentUser?.user) {
      throw new Error('Usuário não autenticado');
    }
    
    console.log(`👤 [releaseSessionPayment] Usuário: ${currentUser.user.id}`);
    console.log(`📝 [releaseSessionPayment] Sessão: ${sessionId}`);
    
    // Obter o token de autenticação
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Token de autenticação não encontrado');
    }
    
    // Chamar a Edge Function que libera o pagamento E envia o email ao mentor
    const { data: result, error } = await supabase.functions.invoke('release-payment', {
      body: { sessionId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
      
    if (error) {
      console.error(`❌ [releaseSessionPayment] Erro na Edge Function:`, error);
      throw new Error(`Erro ao liberar pagamento: ${error.message}`);
    }
    
    console.log(`📊 [releaseSessionPayment] Resultado da Edge Function:`, result);
    
    if (!result?.success) {
      console.error(`❌ [releaseSessionPayment] Edge Function retornou erro:`, result?.error);
      throw new Error(result?.error || 'Erro desconhecido ao liberar pagamento');
    }
    
    console.log(`✅ [releaseSessionPayment] Pagamento liberado com sucesso!`);
    console.log(`📧 [releaseSessionPayment] Email de notificação enviado ao mentor`);
    
  } catch (error) {
    console.error(`❌ [releaseSessionPayment] Erro geral:`, error);
    throw error;
  }
}

/**
 * Verifica se uma sessão já foi avaliada pelo jogador
 */
export async function checkSessionReview(sessionId: string, playerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("session_reviews")
      .select("id")
      .eq("session_id", sessionId)
      .eq("player_id", playerId)
      .single();
      
    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error(`❌ [checkSessionReview] Erro ao verificar avaliação:`, error);
      throw error;
    }
    
    return !!data;
  } catch (error) {
    console.error(`❌ [checkSessionReview] Erro geral:`, error);
    return false;
  }
} 